import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'

import {getAnonymousId} from '@daedalus/core/src/_web/anonymousId'
import {getQueryStringObject} from '@daedalus/core/src/_web/utils/url'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {defaultLanguage} from '@daedalus/core/src/localization/services/locale'
import {Room, RoomOffer} from '@findhotel/sapi'

import {getApplication, getBookingStatus} from '../../modules/selector'
import {getBookingManagementUrl} from '../../utils/url'
import {HotelCard} from '../HotelCard'
import {GradientBackground} from './GradientBackground'
import {HeaderContent} from './HeaderContent'
import {NextSteps} from './NextSteps'

const Wrapper = styled.div`
  position: relative;
`

const ContentContainer = styled.div`
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500}
    ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500};
  z-index: 2;
  position: relative;

  &.isOverlayContent {
    padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s700}
      ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s700};
  }
`

const CloseButtonWrapper = styled.div`
  display: flex;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s250} 0;
  z-index: 2;
  flex-direction: row-reverse;
`

const CloseButton = styled(Button)`
  opacity: 0.6;
`

interface Props {
  expiresAt: string
  hotelName: string
  checkIn: string
  checkOut: string
  roomsSplit: string
  imageUrl: string
  rating: number | undefined
  room: Room | undefined
  errored: boolean
  offer: RoomOffer | undefined
  isOverlayContent?: boolean
  onCloseOverlay: () => void
}
export const ConfirmationContent: React.FC<Props> = ({
  expiresAt,
  errored,
  hotelName,
  checkIn,
  checkOut,
  offer,
  room,
  imageUrl,
  rating,
  roomsSplit,
  isOverlayContent,
  onCloseOverlay
}) => {
  const urlParams = getQueryStringObject()
  const bookingStatus = useSelector(getBookingStatus)
  const {bookingManagementUrl} = useSelector(getApplication)

  const anonymousId = getAnonymousId()
  const bookingId = bookingStatus?.id

  const handleViewFrozenDeals = () => {
    if (!bookingId || !bookingManagementUrl) return

    const myBookingUrl = getBookingManagementUrl({
      hostUrl: bookingManagementUrl,
      bookingId,
      anonymousId,
      locale: (urlParams.locale as string) || defaultLanguage
    })

    location.assign(myBookingUrl)
  }

  return (
    <Wrapper>
      <GradientBackground />
      <CloseButtonWrapper>
        <CloseButton
          variant="transparent"
          iconStart={<Icon name={'Close'} />}
          onClick={onCloseOverlay}
        />
      </CloseButtonWrapper>
      <ContentContainer className={cx(isOverlayContent && 'isOverlayContent')}>
        <HeaderContent expiresAt={expiresAt} />
        <Box paddingTop="s500" paddingBottom="s600">
          <HotelCard
            hotelName={hotelName}
            checkIn={checkIn}
            checkOut={checkOut}
            roomsSplit={roomsSplit}
            room={room}
            offer={offer}
            imageUrl={imageUrl}
            rating={rating}
            shouldShowPayNow={false}
          />
        </Box>
        {!errored && (
          <div>
            <NextSteps expiresAt={expiresAt} />
            <Button
              dataId="ViewFrozenDeal"
              variant="secondary"
              fullWidth
              size="xl"
              onClick={handleViewFrozenDeals}
            >
              <FormattedMessageWrapper
                id="dealFreeze.viewFrozenDeals"
                defaultMessage="Manage frozen deals"
              />
            </Button>
          </div>
        )}
      </ContentContainer>
    </Wrapper>
  )
}
